import React, { useState, useEffect } from 'react';
import { useAccount } from '../../../contexts/AccountContext';
import { useAccountType } from '../../../contexts/AccountTypeContext';
import { aiTradingService, HeartStatus } from '../../../services/aiTradingService';

interface HugeHugStrategyProps {
  symbol: string;
  currentPrice?: string;
  onSignalUpdate?: (signal: 'buy' | 'sell' | null) => void;
  onConfigUpdate?: (config: any) => void;
  isRunning?: boolean;
  initialConfig?: HugeHugConfig; // Add prop for initial configuration
}

interface HugeHugConfig {
  isActive: boolean;
  tradeAmountPercent: number;
  quantity: number;
  profitThreshold: number;
  lossThreshold: number;
  levelOffset: number;
  // HUGE♥︎HUG Strategy Configuration
  initialEntryLevel: string;
  vidyaLength: number;
  vidyaMomentum: number;
  bandDistance: number;
  showSignals: boolean;
  showStrategyPoints: boolean;
  showStrategyAI: boolean;
}

const DEFAULT_CONFIG: HugeHugConfig = {
  isActive: false,
  tradeAmountPercent: 100,
  quantity: 3.33,
  profitThreshold: 9.99,
  lossThreshold: 9.99,
  levelOffset: 1,
  // Matches the Pine default and the entry timeframe the strategy is meant to run on.
  initialEntryLevel: '1m',
  // vidyaLength: 14,
  vidyaLength: 14,
  // vidyaMomentum: 20,
  vidyaMomentum: 20,
  // bandDistance: 3.0,
  bandDistance: 3,
  showSignals: true,
  showStrategyPoints: false,
  showStrategyAI: true
};

const withDefaults = (config?: HugeHugConfig): HugeHugConfig => ({
  isActive: config?.isActive ?? DEFAULT_CONFIG.isActive,
  tradeAmountPercent: config?.tradeAmountPercent ?? DEFAULT_CONFIG.tradeAmountPercent,
  quantity: config?.quantity ?? DEFAULT_CONFIG.quantity,
  profitThreshold: config?.profitThreshold ?? DEFAULT_CONFIG.profitThreshold,
  lossThreshold: config?.lossThreshold ?? DEFAULT_CONFIG.lossThreshold,
  levelOffset: config?.levelOffset ?? DEFAULT_CONFIG.levelOffset,
  initialEntryLevel: config?.initialEntryLevel ?? DEFAULT_CONFIG.initialEntryLevel,
  vidyaLength: config?.vidyaLength ?? DEFAULT_CONFIG.vidyaLength,
  vidyaMomentum: config?.vidyaMomentum ?? DEFAULT_CONFIG.vidyaMomentum,
  bandDistance: config?.bandDistance ?? DEFAULT_CONFIG.bandDistance,
  showSignals: config?.showSignals ?? DEFAULT_CONFIG.showSignals,
  showStrategyPoints: config?.showStrategyPoints ?? DEFAULT_CONFIG.showStrategyPoints,
  showStrategyAI: config?.showStrategyAI ?? DEFAULT_CONFIG.showStrategyAI
});

/**
 * Configuration panel for HUGE♥HUG, plus a readout of the level the strategy is currently
 * watching.
 *
 * This component used to carry a second copy of the strategy and call `executeAITrade`
 * itself, on a 30-second timer fed by the 24-hour ticker price. That meant the rules only
 * ran while this dialog was open, they ran on sampled prices rather than candle closes,
 * and when they did run they competed with the service for the same symbol. Signals now
 * come from `aiTradingService`, which watches closed bars on the configured entry
 * timeframe, and this panel only reads its state.
 */
const HugeHugStrategy: React.FC<HugeHugStrategyProps> = ({
  symbol,
  onConfigUpdate,
  isRunning: externalIsRunning,
  initialConfig
}) => {
  const { getAccountBalance } = useAccount();
  const { accountType } = useAccountType();

  const accountBalance = getAccountBalance(accountType);

  const [config, setConfig] = useState<HugeHugConfig>(() => withDefaults(initialConfig));
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const [heart, setHeart] = useState<HeartStatus | null>(null);

  const actualIsRunning = externalIsRunning ?? false;

  useEffect(() => {
    if (initialConfig && !hasUserEdited) {
      setConfig(withDefaults(initialConfig));
    }
  }, [initialConfig, hasUserEdited]);

  // Notify parent component when config changes
  useEffect(() => {
    onConfigUpdate?.(config);
  }, [config, onConfigUpdate]);

  // Poll the engine's current level. The engine itself only recomputes when a bar closes;
  // this is just how the panel picks that up.
  useEffect(() => {
    if (!actualIsRunning) {
      setHeart(null);
      return;
    }

    setHeart(aiTradingService.getHeartStatus(symbol));
    const interval = setInterval(() => {
      setHeart(aiTradingService.getHeartStatus(symbol));
    }, 2000);

    return () => clearInterval(interval);
  }, [actualIsRunning, symbol]);

  const handleConfigChange = (field: keyof HugeHugConfig, value: number | boolean | string) => {
    setHasUserEdited(true); // Mark that user has made changes
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const renderHeartStatus = () => {
    if (!heart) {
      return (
        <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
          Loading {config.initialEntryLevel} candles…
        </div>
      );
    }

    if (heart.candleCount < heart.requiredCandles) {
      return (
        <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
          Warming up: {heart.candleCount}/{heart.requiredCandles} {heart.interval} candles
        </div>
      );
    }

    if (heart.level === null) {
      return (
        <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
          Waiting for the first band cross on {heart.interval} to set the trend
        </div>
      );
    }

    return (
      <div className="mt-1 text-xs">
        <span className={heart.isTrendUp ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
          {heart.isTrendUp ? '♥ Green' : '♥ Red'} heart at {heart.level.toFixed(4)}
        </span>
        <span className="text-gray-600 dark:text-gray-400">
          {' '}· {heart.interval} close {heart.close.toFixed(4)}
        </span>
        <div className="text-gray-600 dark:text-gray-400">
          {heart.isTrendUp ? 'Buying' : 'Selling'} the turn, then again while it stays{' '}
          {heart.isTrendUp ? 'green' : 'red'} on a break{' '}
          {heart.isTrendUp
            ? 'above a green candle that follows three red'
            : 'below a red candle that follows three green'}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Strategy Status */}
      <div className="p-3 bg-blue-50 dark:bg-blue-100/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex items-center space-x-2">
          <div
            className={`w-2 h-2 rounded-full ${actualIsRunning ? 'bg-green-500 animate-pulse' : 'bg-blue-500'}`}
          />
          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
            {actualIsRunning
              ? `Watching ${config.initialEntryLevel} candles on ${symbol}`
              : 'HUGE♥︎HUG Strategy Configured - Ready to Save & Start'}
          </span>
        </div>
        {actualIsRunning ? (
          renderHeartStatus()
        ) : (
          <div className="mt-1 text-xs text-green-600 dark:text-green-400">
            ✅ Strategy will run independently after saving
          </div>
        )}
      </div>

      {/* Strategy Configuration */}
      <div className="space-y-3">
        
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Trade Amount:</span>
          <div className="flex items-center space-x-1 w-[30%]">
            <span className="text-xs theme-text-quaternary">%</span>
            <input
              type="number"
              value={config.tradeAmountPercent}
              onChange={(e) => handleConfigChange('tradeAmountPercent', parseFloat(e.target.value))}
              className="flex-1 px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="1"
              max="100"
              step="1"
            />
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Quantity:</span>
          <div className="flex items-center space-x-1 w-[30%]">
            <input
              type="number"
              value={config.quantity}
              onChange={(e) => handleConfigChange('quantity', parseFloat(e.target.value))}
              className="flex-1 px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="0.01"
              max="1000"
              step="0.01"
            />
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Profit Threshold:</span>
          <div className="flex items-center space-x-1 w-[30%]">
            <input
              type="number"
              value={config.profitThreshold}
              onChange={(e) => handleConfigChange('profitThreshold', parseFloat(e.target.value))}
              className="flex-1 px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="0.0001"
              max="50000"
              step="0.0001"
            />
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Loss Threshold:</span>
          <div className="flex items-center space-x-1 w-[30%]">
            <input
              type="number"
              value={config.lossThreshold}
              onChange={(e) => handleConfigChange('lossThreshold', parseFloat(e.target.value))}
              className="flex-1 px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="0.0001"
              max="50000"
              step="0.0001"
            />
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Level Offset:</span>
          <div className="flex items-center space-x-1 w-[30%]">
            <input
              type="number"
              value={config.levelOffset}
              onChange={(e) => handleConfigChange('levelOffset', parseFloat(e.target.value))}
              className="flex-1 px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="0.0001"
              max="50000"
              step="0.0001"
            />
          </div>
        </div>
        
        {/* HUGE♥︎HUG Strategy Configuration */}
        <div className="border-t theme-border pt-3 mt-3">
          <div className="text-xs font-medium theme-text-secondary mb-2">HUGE♥︎HUG Strategy</div>
          
          <div className="flex items-center justify-between">
            <span className="text-xs theme-text-quaternary">Initial Entry Level:</span>
            <select
              value={config.initialEntryLevel}
              onChange={(e) => handleConfigChange('initialEntryLevel', e.target.value)}
              className="w-[30%] px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary"
            >
              <option value="1m">1m</option>
              <option value="5m">5m</option>
              <option value="15m">15m</option>
              <option value="1h">1h</option>
              <option value="1d">1d</option>
              <option value="1w">1w</option>
              <option value="1M">1M</option>
            </select>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-xs theme-text-quaternary">VIDYA Length:</span>
            <input
              type="number"
              value={config.vidyaLength}
              onChange={(e) => handleConfigChange('vidyaLength', parseInt(e.target.value))}
              className="w-[30%] px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="1"
              max="50"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-xs theme-text-quaternary">VIDYA Momentum:</span>
            <input
              type="number"
              value={config.vidyaMomentum}
              onChange={(e) => handleConfigChange('vidyaMomentum', parseInt(e.target.value))}
              className="w-[30%] px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="1"
              max="50"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-xs theme-text-quaternary">Band Distance:</span>
            <input
              type="number"
              value={config.bandDistance}
              onChange={(e) => handleConfigChange('bandDistance', parseFloat(e.target.value))}
              className="w-[30%] px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min="0.1"
              max="10"
              step="0.1"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-xs theme-text-quaternary">Show Signals:</span>
            <input
              type="checkbox"
              checked={config.showSignals}
              onChange={(e) => handleConfigChange('showSignals', e.target.checked)}
              className="w-[30%]"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-xs theme-text-quaternary">Strategy Points:</span>
            <input
              type="checkbox"
              checked={config.showStrategyPoints}
              onChange={(e) => handleConfigChange('showStrategyPoints', e.target.checked)}
              className="w-[30%]"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-xs theme-text-quaternary">AI Strategy:</span>
            <input
              type="checkbox"
              checked={config.showStrategyAI}
              onChange={(e) => handleConfigChange('showStrategyAI', e.target.checked)}
              className="w-[30%]"
            />
          </div>
        </div>
        
        {/* Trade Amount Display */}
        {accountBalance && (
          <div className="flex items-center justify-between pt-2 border-t theme-border">
            <span className="text-xs theme-text-quaternary">Trade Value:</span>
            <span className="text-xs font-medium theme-text-primary">
              ${(accountBalance.totalBalance * config.tradeAmountPercent / 100).toFixed(2)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default HugeHugStrategy;
/* HUGE♥︎HUG Strategy - Based on LiveTradingStudio VidyaHeart Multi-Timeframe Concept */
